// Orquestador del version-guard.
//
// Junta las piezas y decide QUE hacer y CUANDO. El diseño parte de una
// premisa: una SPA cargada no vuelve a pedir el HTML nunca mas, asi que
// ninguna cabecera HTTP puede sacarla de una version vieja. La app tiene que
// preguntar. Todo lo demas se deriva de eso.
//
// Disparadores de chequeo (redundantes a proposito — cada uno cubre un hueco
// que los otros no ven):
//
//   - al arrancar (con un retraso corto, para no competir con la carga)
//   - intervalo, solo mientras la pestaña esta visible
//   - la pestaña vuelve a ser visible  → cubre "la tenia abierta hace dias"
//   - focus de la ventana              → cubre gestores de ventanas raros
//   - pageshow con persisted=true      → cubre bfcache (atras/adelante)
//   - online                           → cubre volver de estar sin red
//   - navegacion de ruta (throttled)   → cubre sesiones largas de uso activo
//   - aviso de otra pestaña            → una descubre, todas se enteran
//
// Escalada, para que "hay una version nueva" no sea ignorable para siempre:
//
//   recommended → banner descartable
//     ...pasa escalateAfterMs con el update pendiente...
//   recommended → banner NO descartable
//     ...la pestaña queda ociosa o se oculta, y no hay trabajo a medias...
//   → recarga automatica
//
//   forced (por severity o por minBuildId) saltea todo y recarga ya.

import { isSafeToReload, startActivityTracking } from './activity';
import { createChannel } from './channel';
import { belowMinimum } from './parse';
import { probeRemote } from './probe';
import { clearPendingReload, executeReload, resolvePendingReload } from './reload';
import { readLocalStamp } from './stamp';
import type { GuardState, RemoteStamp, UpdatePolicy, UpdateSeverity } from './types';

export interface GuardOptions {
  portal: string;
  policy?: UpdatePolicy;
  /** Cada cuanto chequear con la pestaña visible. */
  pollIntervalMs?: number;
  /** Chequeo al volver a visible solo si el ultimo es mas viejo que esto. */
  visibilityMinAgeMs?: number;
  /** Minimo entre chequeos disparados por navegacion. */
  navigationThrottleMs?: number;
  /** Con un update pendiente tanto tiempo, el banner deja de cerrarse. */
  escalateAfterMs?: number;
  /** Ociosidad requerida para recargar sin preguntar. */
  idleReloadAfterMs?: number;
  /**
   * La URL de este portal puede llevar estado de un solo uso (codigos OAuth,
   * tokens de verificacion). Si es true, nunca recargamos solos una URL con
   * query o hash: mostramos el banner y que decida el usuario.
   */
  urlStateIsFragile?: boolean;
  /** Base del portal (con barra final). */
  base?: string;
}

const DEFAULTS = {
  policy: 'banner' as UpdatePolicy,
  pollIntervalMs: 120_000,
  visibilityMinAgeMs: 15_000,
  navigationThrottleMs: 30_000,
  escalateAfterMs: 30 * 60_000,
  idleReloadAfterMs: 60_000,
  urlStateIsFragile: false,
  base: '/',
};

export interface VersionGuard {
  getState: () => GuardState;
  subscribe: (fn: (state: GuardState) => void) => () => void;
  /** Chequeo manual (boton "buscar actualizaciones"). */
  checkNow: () => Promise<void>;
  /** Chequeo por cambio de ruta, con throttle propio. */
  checkOnNavigation: () => void;
  /** Aplica la actualizacion ya, escalera incluida. */
  applyUpdate: () => void;
  /** Cierra el banner, si todavia se puede. */
  dismiss: () => void;
  stop: () => void;
}

export function startVersionGuard(options: GuardOptions): VersionGuard {
  const opts = { ...DEFAULTS, ...options };
  const local = readLocalStamp();

  const state: GuardState = {
    status: 'idle',
    local,
    remote: null,
    severity: 'recommended',
    dismissible: true,
    dismissed: false,
    detectedAt: null,
    lastCheckAt: null,
    lastOkCheckAt: null,
    consecutiveFailures: 0,
    justUpdated: null,
  };

  const listeners = new Set<(s: GuardState) => void>();
  const emit = (): void => {
    for (const fn of listeners) fn({ ...state });
  };

  // --- Paso 1: venimos de una recarga nuestra? --------------------------
  //
  // Esto corre antes que cualquier chequeo. Decide el toast de exito y, si el
  // browser nos devolvio la misma version, escala en el acto.
  const verdict = resolvePendingReload(local.buildId);
  if (verdict.kind === 'updated') {
    state.justUpdated = verdict.outcome;
  } else if (verdict.kind === 'failed') {
    // El reload no trajo nada nuevo. Subimos un escalon inmediatamente.
    void executeReload({
      from: verdict.from,
      target: verdict.target,
      attempt: verdict.nextAttempt,
    });
  } else if (verdict.kind === 'exhausted') {
    state.status = 'stuck';
    state.severity = 'forced';
    state.dismissible = false;
  }

  const stopActivity = startActivityTracking();

  const channel = createChannel(opts.portal, (msg) => {
    // Otra pestaña encontro algo. Confiamos y evaluamos con nuestro propio
    // buildId (podriamos ser una pestaña mas nueva que la que aviso).
    applyRemote(msg.stamp, { broadcast: false });
  });

  let stopped = false;
  let inFlight: Promise<void> | null = null;
  let lastNavigationCheck = 0;
  let escalationTimer: ReturnType<typeof setTimeout> | null = null;
  /** Ultimo buildId por el que ya avisamos — distingue deploy nuevo de re-aviso. */
  let announcedBuildId: string | null = null;

  function scheduleEscalation(): void {
    if (escalationTimer !== null) return;
    escalationTimer = setTimeout(() => {
      escalationTimer = null;
      if (state.status !== 'update-available') return;
      // El banner deja de poder cerrarse. No recargamos de prepo: seguimos
      // esperando un momento seguro, pero ya no se puede ignorar visualmente.
      state.dismissible = false;
      state.dismissed = false;
      emit();
      maybeAutoReload();
    }, opts.escalateAfterMs);
  }

  function maybeAutoReload(): void {
    if (stopped || state.status !== 'update-available' || !state.remote) return;

    const forced = state.severity === 'forced';
    const autoPolicy = opts.policy === 'auto-when-idle';
    // El banner no-descartable (post escalada) tambien habilita la recarga
    // oportunista: es la unica forma de converger con un usuario que ignora.
    const wantsAuto = forced || autoPolicy || !state.dismissible;
    if (!wantsAuto) return;

    // `forced` no espera a nada: es para un fix que no puede seguir roto.
    if (!forced && !isSafeToReload(opts.idleReloadAfterMs, opts.urlStateIsFragile)) return;

    state.status = 'reloading';
    emit();
    void executeReload({ from: local.buildId, target: state.remote.buildId, attempt: 0 });
  }

  function applyRemote(remote: RemoteStamp, { broadcast }: { broadcast: boolean }): void {
    if (stopped) return;

    state.remote = remote;
    state.lastOkCheckAt = Date.now();
    state.consecutiveFailures = 0;

    // Sin sello propio no hay comparacion posible. Preferimos callarnos antes
    // que mostrar un banner permanente que nadie puede resolver.
    const comparable = local.buildId !== 'unknown';
    const differs = comparable && remote.buildId !== local.buildId;
    // El caso 'asset-drift:same' es explicitamente "no hay nada nuevo".
    const sameByDrift = remote.buildId === 'asset-drift:same';

    if (!differs || sameByDrift) {
      if (state.status === 'update-available') {
        // Rollback: volvieron a la version que ya tenemos. Bajamos el cartel.
        state.status = 'idle';
        state.detectedAt = null;
        state.dismissed = false;
        state.dismissible = true;
        announcedBuildId = null;
      } else if (state.status !== 'stuck' && state.status !== 'reloading') {
        state.status = 'idle';
      }
      emit();
      return;
    }

    const severity: UpdateSeverity =
      remote.severity === 'forced' ||
      belowMinimum(local.buildId, remote.buildId, remote.minBuildId)
        ? 'forced'
        : 'recommended';

    // Un buildId distinto al que ya veniamos anunciando es un deploy nuevo, no
    // el mismo aviso: si el usuario habia cerrado el banner anterior, este se
    // vuelve a mostrar. Sin esto, dos deploys seguidos dejarian el segundo
    // silenciado por un "Después" que era para el primero.
    const isNew = announcedBuildId !== remote.buildId;
    if (isNew) {
      state.detectedAt = Date.now();
      state.dismissed = false;
    }
    announcedBuildId = remote.buildId;
    state.status = 'update-available';
    state.severity = severity;
    if (severity === 'forced') state.dismissible = false;

    emit();
    if (broadcast) channel.post(remote);
    scheduleEscalation();
    maybeAutoReload();
  }

  async function check(): Promise<void> {
    if (stopped || state.status === 'reloading') return;
    if (inFlight) return inFlight;

    const run = async (): Promise<void> => {
      state.lastCheckAt = Date.now();
      if (state.status === 'idle') {
        state.status = 'checking';
        emit();
      }

      const result = await probeRemote(opts.base);
      if (stopped) return;

      if (result.ok) {
        applyRemote(result.stamp, { broadcast: true });
      } else {
        state.consecutiveFailures += 1;
        if (state.status === 'checking') state.status = 'idle';
        // Silencioso: no hay nada util que decirle al usuario si no pudimos
        // consultar. La proxima corrida reintenta.
        emit();
      }
    };

    inFlight = run().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  // --- Disparadores -----------------------------------------------------

  const timers: Array<ReturnType<typeof setTimeout>> = [];

  // Arranque diferido: la carga inicial ya tiene bastante trabajo.
  timers.push(setTimeout(() => void check(), 3_000));

  const interval = setInterval(() => {
    if (document.visibilityState === 'visible') void check();
  }, opts.pollIntervalMs);

  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') {
      // Se fue a segundo plano con un update pendiente: momento ideal para
      // aplicarlo sin que se note.
      maybeAutoReload();
      return;
    }
    const age = state.lastCheckAt === null ? Infinity : Date.now() - state.lastCheckAt;
    if (age >= opts.visibilityMinAgeMs) void check();
  };

  const onFocus = (): void => {
    const age = state.lastCheckAt === null ? Infinity : Date.now() - state.lastCheckAt;
    if (age >= opts.visibilityMinAgeMs) void check();
  };

  // bfcache: la pagina se restaura desde memoria, sin ningun request. Es el
  // caso de "cierro y abro el navegador" / atras-adelante.
  const onPageShow = (ev: PageTransitionEvent): void => {
    if (ev.persisted) void check();
  };

  const onOnline = (): void => void check();

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('online', onOnline);

  // Uso activo prolongado: alguien que navega el backoffice toda la tarde sin
  // que la pestaña pierda el foco no dispararia ninguno de los otros eventos.
  function checkOnNavigation(): void {
    const now = Date.now();
    if (now - lastNavigationCheck < opts.navigationThrottleMs) return;
    lastNavigationCheck = now;
    void check();
  }

  // Enganche a la navegacion del router sin depender de el: react-router
  // llama a history.pushState. Envolvemos los dos metodos y escuchamos
  // popstate. Los originales se restauran en stop().
  const originalPush = window.history.pushState.bind(window.history);
  const originalReplace = window.history.replaceState.bind(window.history);
  window.history.pushState = function patchedPush(...args) {
    const out = originalPush(...args);
    checkOnNavigation();
    return out;
  };
  window.history.replaceState = function patchedReplace(...args) {
    const out = originalReplace(...args);
    checkOnNavigation();
    return out;
  };
  const onPopState = (): void => checkOnNavigation();
  window.addEventListener('popstate', onPopState);

  return {
    getState: () => ({ ...state }),

    subscribe(fn) {
      listeners.add(fn);
      fn({ ...state });
      return () => listeners.delete(fn);
    },

    async checkNow() {
      await check();
    },

    checkOnNavigation,

    applyUpdate() {
      if (!state.remote) return;
      state.status = 'reloading';
      emit();
      void executeReload({ from: local.buildId, target: state.remote.buildId, attempt: 0 });
    },

    dismiss() {
      if (!state.dismissible) return;
      state.dismissed = true;
      emit();
    },

    stop() {
      stopped = true;
      clearInterval(interval);
      for (const t of timers) clearTimeout(t);
      if (escalationTimer !== null) clearTimeout(escalationTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('popstate', onPopState);
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      channel.close();
      stopActivity();
      clearPendingReload();
      listeners.clear();
    },
  };
}
